import { RootState } from 'types/state'
import { createCachedReduxSelector } from 'util/createCachedReduxSelector'
import {
  getDefaultButtonLabel,
  isActionWhitelisted,
  requiresConfirmation,
} from '../layout/widgets/adaChat/actionPermissions'

export const SET_CHAT_DRAWER_OPEN = 'SET_CHAT_DRAWER_OPEN'
export const ADD_MESSAGE = 'ADD_MESSAGE'
export const EXECUTE_ACTION = 'EXECUTE_ACTION'
export const MESSAGE_CHUNK = 'MESSAGE_CHUNK'
export const CLEAR_MESSAGES = 'CLEAR_MESSAGES'
export const ADA_STATUS_UPDATE = 'ADA_STATUS_UPDATE'
export const REMOVE_LAST_MESSAGE = 'REMOVE_LAST_MESSAGE'
export const CLEAR_ACTIONS = 'CLEAR_ACTIONS'
export const SET_VOICE_SESSION_ACTIVE = 'SET_VOICE_SESSION_ACTIVE'
export const STOP_VOICE_SESSION = 'STOP_VOICE_SESSION'
export const SET_APP_STATE = 'SET_APP_STATE'
export const MARK_MESSAGES_AS_READ = 'MARK_MESSAGES_AS_READ'
export const IMPORT_VOICE_MESSAGES = 'IMPORT_VOICE_MESSAGES'
export const MARK_MESSAGES_SHARED_WITH_CONTEXT = 'MARK_MESSAGES_SHARED_WITH_CONTEXT'
export const RESET_CHAT_STATE = 'RESET_CHAT_STATE'
export const MARK_MESSAGE_FEEDBACK_SUBMITTED = 'MARK_MESSAGE_FEEDBACK_SUBMITTED'
export const MARK_MESSAGE_INDEX_FAILED = 'MARK_MESSAGE_INDEX_FAILED'
export const CLEAR_MESSAGE_INDEX_FAILED = 'CLEAR_MESSAGE_INDEX_FAILED'
export const SET_SEND_FAILED = 'SET_SEND_FAILED'
export const CLEAR_SEND_FAILED = 'CLEAR_SEND_FAILED'
export const UPDATE_TOOL_EXECUTION_STATE = 'UPDATE_TOOL_EXECUTION_STATE'
export const UPDATE_BUTTON_ACTION_STATE = 'UPDATE_BUTTON_ACTION_STATE'
export const SET_CHAT_HEADER_MENU_OPEN = 'SET_CHAT_HEADER_MENU_OPEN'
export const SET_CHAT_SUGGESTIONS_PREFERENCES = 'SET_CHAT_SUGGESTIONS_PREFERENCES'

export interface ButtonAction {
  label: string
  action: any
  isCollapsed?: boolean
  confirmedActionLabel?: string | null
  isAutoAccepted?: boolean // True if action was auto-accepted via whitelist
}

// Mapping from tool names to actual action names (in case LLM sends wrong name)
const getToolNameToActionNameMapping = (): Record<string, string> => ({
  generate_run_auto_string_action: 'runAutoString',
  generate_auto_design_action: 'autoDesignEphemeral',
  generate_run_battery_design_assistant_action: 'runBatteryDesignAssistant',
  generate_run_inverter_design_assistant_action: 'runInverterDesignAssistant',
  generate_system_edit_action: 'systemEdit',
  generate_navigate_action: 'navigate',
  generate_save_system_design_as_template_action: 'saveSystemDesignAsTemplate',
  generate_load_auto_design_result_action: 'loadAutoDesignResult',
  generate_load_battery_design_assistant_result_action: 'loadBatteryDesignAssistantResult',
  generate_undo_redo_action: 'undoRedo',
})

const extractActionDataFromJson = (jsonObject: any): any => {
  let actionData = jsonObject
  if (jsonObject.action_json && typeof jsonObject.action_json === 'object') {
    actionData = jsonObject.action_json
  }
  return actionData
}

const normalizeActionName = (actionData: any, toolNameToActionName: Record<string, string>): any => {
  if (actionData && actionData.action && toolNameToActionName[actionData.action]) {
    if (window.debugAdaActions) {
      console.warn(
        `[ADA DEBUG] Fixed action name from tool name "${actionData.action}" to "${
          toolNameToActionName[actionData.action]
        }"`
      )
    }
    return {
      ...actionData,
      action: toolNameToActionName[actionData.action],
    }
  }
  return actionData
}

const parseJsonBlock = (
  match: RegExpExecArray,
  toolNameToActionName: Record<string, string>
): { actionData: any | null; cleanedBlock: string } => {
  try {
    const jsonString = match[1].trim()
    const jsonObject = JSON.parse(jsonString)

    let actionData = extractActionDataFromJson(jsonObject)
    actionData = normalizeActionName(actionData, toolNameToActionName)

    if (actionData && actionData.hasOwnProperty('action') && actionData.hasOwnProperty('args')) {
      return { actionData, cleanedBlock: match[0] }
    } else {
      console.warn("Invalid JSON block: Missing 'action' or 'args' property", {
        original: jsonObject,
        extracted: actionData,
        jsonString: jsonString.substring(0, 200),
      })
      return { actionData: null, cleanedBlock: match[0] }
    }
  } catch (error) {
    console.error('Failed to parse JSON block:', error, {
      match: match[0]?.substring(0, 200),
      jsonString: match[1]?.substring(0, 200),
    })
    return { actionData: null, cleanedBlock: match[0] }
  }
}

const extractJsonBlocksFromMessage = (
  message: string,
  toolNameToActionName: Record<string, string>
): { jsonBlocks: any[]; cleanedMessage: string } => {
  const jsonBlocks: any[] = []
  const jsonBlockRegex = /```json\s*([\s\S]*?)```/g
  let match
  let cleanedMessage = message

  while ((match = jsonBlockRegex.exec(message)) !== null) {
    const { actionData, cleanedBlock } = parseJsonBlock(match, toolNameToActionName)
    if (actionData) {
      jsonBlocks.push(actionData)
    }
    cleanedMessage = cleanedMessage.replace(cleanedBlock, '')
  }

  return { jsonBlocks, cleanedMessage }
}

const getButtonLabel = (jsonBlock: any, actionType: string, hasExplicitButtonLabel: boolean): string => {
  return hasExplicitButtonLabel ? jsonBlock.buttonLabel : getDefaultButtonLabel(actionType) || 'Confirm'
}

const createButtonAction = (
  jsonBlock: any,
  buttonLabel: string,
  isAutoAccepted: boolean,
  isCollapsed?: boolean
): ButtonAction => {
  const buttonAction: ButtonAction = {
    label: buttonLabel,
    action: { action: jsonBlock.action, args: jsonBlock.args },
    isAutoAccepted,
  }
  if (isCollapsed !== undefined) {
    buttonAction.isCollapsed = isCollapsed
    buttonAction.confirmedActionLabel = buttonLabel
  }
  return buttonAction
}

const categorizeAction = (jsonBlock: any, buttonActions: ButtonAction[], actions: any[]): void => {
  const actionType = jsonBlock.action
  const hasExplicitButtonLabel = jsonBlock.buttonLabel != null && jsonBlock.buttonLabel !== ''
  const requiresConfirmationFromRegistry = requiresConfirmation(actionType)
  const needsConfirmation = hasExplicitButtonLabel || requiresConfirmationFromRegistry
  const isWhitelisted = isActionWhitelisted(actionType)

  if (needsConfirmation && !isWhitelisted) {
    const buttonLabel = getButtonLabel(jsonBlock, actionType, hasExplicitButtonLabel)

    if (window.adaDebug) {
      console.log('Action requires confirmation:', {
        actionType,
        hasExplicitButtonLabel,
        requiresConfirmationFromRegistry,
        buttonLabel,
      })
    }

    buttonActions.push(createButtonAction(jsonBlock, buttonLabel, false))
  } else {
    if (isWhitelisted && needsConfirmation) {
      const buttonLabel = getButtonLabel(jsonBlock, actionType, hasExplicitButtonLabel)
      buttonActions.push(createButtonAction(jsonBlock, buttonLabel, true, true))
      actions.push({ action: jsonBlock.action, args: jsonBlock.args })
    } else {
      if (window.adaDebug) {
        console.log('Action will execute automatically:', {
          actionType,
          hasExplicitButtonLabel,
          requiresConfirmationFromRegistry,
          isWhitelisted,
        })
      }
      actions.push({ action: jsonBlock.action, args: jsonBlock.args })
    }
  }
}

const processActions = (jsonBlocks: any[]): { actions: any[]; buttonActions: ButtonAction[] } => {
  const buttonActions: ButtonAction[] = []
  const actions: any[] = []

  // Actions can either execute automatically or after a confirmation click.
  // If an action type is marked as requiring confirmation in the permission registry,
  // it will automatically require confirmation even if buttonLabel is not provided.
  // However, if the action is whitelisted, it will be auto-accepted.
  jsonBlocks.forEach((jsonBlock) => {
    categorizeAction(jsonBlock, buttonActions, actions)
  })

  return { actions, buttonActions }
}

export const extractActionsFromMessage = (message: string) => {
  const toolNameToActionName = getToolNameToActionNameMapping()
  const { jsonBlocks, cleanedMessage } = extractJsonBlocksFromMessage(message, toolNameToActionName)
  const { actions, buttonActions } = processActions(jsonBlocks)

  // Do not trim cleanedMessage because it may include intentional leading/trailing spaces
  // that must be retained so they can be merged with other chunks.
  return { actions, cleanedMessage, buttonActions }
}

export const chatDrawerActions = {
  setChatDrawerOpen: (chatDrawerOpen: boolean) => ({
    type: SET_CHAT_DRAWER_OPEN,
    chatDrawerOpen,
  }),
  addMessage: (
    content: string,
    direction: 'send' | 'receive',
    origin: 'text' | 'voice' | 'app' = 'text',
    fileAttachments?: Array<{ file_id?: string; file_name: string; file_type: string }>
  ) => ({
    type: ADD_MESSAGE,
    content,
    direction,
    origin,
    sharedWithContexts: origin === 'app' ? [] : [origin],
    fileAttachments,
  }),
  clearMessages: () => ({
    type: CLEAR_MESSAGES,
  }),
  removeLastMessage: () => ({
    type: REMOVE_LAST_MESSAGE,
  }),
  clearActions: () => ({
    type: CLEAR_ACTIONS,
  }),
  setVoiceSessionActive: (active: boolean) => ({
    type: SET_VOICE_SESSION_ACTIVE,
    active,
  }),
  stopVoiceSession: () => ({
    type: STOP_VOICE_SESSION,
  }),
  setAppState: (appState: Record<string, any>) => ({
    type: SET_APP_STATE,
    appState,
  }),
  markMessagesAsRead: () => ({
    type: MARK_MESSAGES_AS_READ,
  }),
  importVoiceMessages: (voiceMessages: ChatDrawerMessage[]) => ({
    type: IMPORT_VOICE_MESSAGES,
    voiceMessages,
  }),
  markMessagesSharedWithContext: (context: string, messageIndices?: number[]) => ({
    type: MARK_MESSAGES_SHARED_WITH_CONTEXT,
    context,
    messageIndices,
  }),
  resetChatState: (messages?: ChatDrawerMessage[]) => {
    if (messages) {
      messages.forEach((msg) => {
        if (msg.fileAttachments) {
          msg.fileAttachments.forEach((file) => {
            if (file.previewUrl) {
              URL.revokeObjectURL(file.previewUrl)
            }
          })
        }
      })
    }
    return {
      type: RESET_CHAT_STATE,
    }
  },
  markMessageFeedbackSubmitted: (messageId: number) => ({
    type: MARK_MESSAGE_FEEDBACK_SUBMITTED,
    messageId,
  }),
  markMessageIndexFailed: (messageIndex: number) => ({
    type: MARK_MESSAGE_INDEX_FAILED,
    messageIndex,
  }),
  clearMessageIndexFailed: (messageIndex: number) => ({
    type: CLEAR_MESSAGE_INDEX_FAILED,
    messageIndex,
  }),
  setSendFailed: () => ({
    type: SET_SEND_FAILED,
  }),
  clearSendFailed: () => ({
    type: CLEAR_SEND_FAILED,
  }),
  updateToolExecutionState: (messageIndex: number, toolExecutionState: ToolExecutionState) => ({
    type: UPDATE_TOOL_EXECUTION_STATE,
    messageIndex,
    toolExecutionState,
  }),
  updateButtonActionState: (
    messageIndex: number,
    buttonActionIndex: number,
    isCollapsed: boolean,
    confirmedActionLabel?: string | null
  ) => ({
    type: UPDATE_BUTTON_ACTION_STATE,
    messageIndex,
    buttonActionIndex,
    isCollapsed,
    confirmedActionLabel,
  }),
  setChatHeaderMenuOpen: (open: boolean) => ({
    type: SET_CHAT_HEADER_MENU_OPEN,
    open,
  }),
  setChatSuggestionsPreferences: (preferences: Partial<ChatSuggestionsPreferences>) => ({
    type: SET_CHAT_SUGGESTIONS_PREFERENCES,
    preferences,
  }),
}

export type ToolExecutionStatus = 'running' | 'ready' | 'completed' | 'error'

export interface ToolExecutionState {
  toolType: string
  status: ToolExecutionStatus
  description?: string
  data?: any
  projectId?: number
  identifier?: number
  [key: string]: any
}

export interface ChatDrawerMessage {
  content: string
  direction: string // 'send' | 'receive'
  isRead?: boolean
  origin?: 'text' | 'voice' | 'app' // Track where the message came from
  sharedWithContexts: string[] // Track which contexts this message has been shared with
  buttonActions: ButtonAction[]
  messageId?: number
  toolExecutionState?: ToolExecutionState
  fileAttachments?: Array<{
    file_id?: string
    file_name: string
    file_type: string
    previewUrl?: string
  }>
}

interface AdaAction {
  type: string
  payload: any
}

export interface ChatSuggestionsPreferences {
  showSuggestionsOnEmptyChat: boolean
  showCompactSuggestions: boolean
}

const STORAGE_KEY = 'ada_chat_suggestions_preferences'
const DEFAULT_PREFERENCES: ChatSuggestionsPreferences = {
  showSuggestionsOnEmptyChat: true,
  showCompactSuggestions: false,
}

const getInitialPreferences = (): ChatSuggestionsPreferences => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return DEFAULT_PREFERENCES
    const parsed = JSON.parse(stored) as Partial<ChatSuggestionsPreferences>
    return { ...DEFAULT_PREFERENCES, ...parsed }
  } catch (error) {
    console.error('Error reading chat suggestions preferences from localStorage:', error)
    return DEFAULT_PREFERENCES
  }
}

const initialState: ChatDrawerState = {
  chatDrawerOpen: window.localStorage.getItem('chatDrawerOpen') === 'true' || false,
  messages: [],
  // Store messages received out of sequence so we can process them when the other intermediate message have arrived
  futureMessageChunks: [],
  // Only process the next message that matches sequenceId
  sequenceId: 0,
  streaming: false,
  // List of actions to be executed which is a stack that will be cleared in order.
  actions: [],
  voiceSessionActive: false,
  appState: {},
  adaStatus: null,
  feedbackSubmittedMessageIds: new Set(),
  failedMessageIndices: new Set(),
  sendFailed: false,
  chatHeaderMenuOpen: false,
  chatSuggestionsPreferences: getInitialPreferences(),
}

const processMessageChunk = (previousState, _messageChunkPayload) => {
  let newState: Partial<ChatDrawerState> = {}

  let content = _messageChunkPayload.content

  // Extract any actions from content.
  // We rely on the back-end to always expand any chunks so they do not break any actions across chunks.
  const { actions, cleanedMessage, buttonActions } = extractActionsFromMessage(content)

  if (actions.length > 0) {
    newState = {
      ...newState,
      actions: [...previousState.actions, ...actions],
    }
  }

  let _messageChunkSequenceId = _messageChunkPayload.sequence_id

  // If there are no messages, or the last message was not "received", then add an empty dummy message.
  let lastMessage = previousState.messages.slice(-1)[0]

  if (lastMessage && lastMessage.direction === 'receive') {
    const isRead = previousState.chatDrawerOpen ? true : lastMessage.isRead || false
    const updatedMessage = {
      direction: lastMessage.direction,
      content: lastMessage.content + cleanedMessage,
      isRead: isRead,
      origin: 'text' as const,
      sharedWithContexts: lastMessage.sharedWithContexts || ['text'],
      buttonActions: [...(lastMessage.buttonActions || []), ...buttonActions],
    }

    // we already have a message to append to
    newState = {
      ...newState,
      messages: [...previousState.messages.slice(0, -1), updatedMessage],
      sequenceId: _messageChunkSequenceId + 1,
    }
  } else {
    const newMessage = {
      direction: 'receive',
      content: cleanedMessage,
      isRead: previousState.chatDrawerOpen,
      origin: 'text' as const,
      sharedWithContexts: ['text'],
      buttonActions: buttonActions,
    }

    // in all other scenarios we will append a new message
    newState = {
      ...newState,
      messages: [...previousState.messages, newMessage],
      sequenceId: _messageChunkSequenceId + 1,
    }
  }

  if (_messageChunkPayload.final) {
    // If this is the final chunk, capture the message ID if provided
    if (_messageChunkPayload.message_id && newState.messages) {
      const lastMessageIndex = newState.messages.length - 1
      if (lastMessageIndex >= 0) {
        newState.messages[lastMessageIndex] = {
          ...newState.messages[lastMessageIndex],
          messageId: _messageChunkPayload.message_id,
        }
      }
    }

    newState = {
      ...newState,
      streaming: false,
      futureMessageChunks: [],
      sequenceId: 0,
      adaStatus: null,
    }
  } else if (!previousState.streaming) {
    newState = {
      ...newState,
      streaming: true,
      adaStatus: null,
    }
  }

  newState = {
    ...newState,
    futureMessageChunks: previousState.futureMessageChunks.filter(
      (chunk) => chunk.sequence_id !== _messageChunkSequenceId
    ),
  }

  return {
    ...previousState,
    ...newState,
  }
}

export default (previousState: ChatDrawerState = initialState, payload) => {
  if (payload?.type === SET_CHAT_DRAWER_OPEN) {
    let action = payload
    return {
      ...previousState,
      chatDrawerOpen: action.chatDrawerOpen,
    }
  } else if (payload.type === EXECUTE_ACTION) {
    let action = {
      action: payload.payload.action,
      args: payload.payload.args,
    }
    return {
      ...previousState,
      actions: [...previousState.actions, action],
    }
  } else if (payload.type === ADD_MESSAGE) {
    let message = payload
    const newMessage = {
      ...message,
      isRead: message.direction === 'send' ? true : previousState.chatDrawerOpen,
      origin: message.origin || 'text',
      sharedWithContexts: message.sharedWithContexts || (message.origin === 'app' ? [] : [message.origin || 'text']),
    }

    return {
      ...previousState,
      messages: [...previousState.messages, newMessage],
      sendFailed: false, // Reset send failed when new message is sent
      futureMessageChunks: [],
      sequenceId: 0,
    }
  } else if (payload.type === MESSAGE_CHUNK) {
    let newMessageChunkPayload = payload.args[0]
    let newMessageChunkSequenceId = newMessageChunkPayload.sequence_id

    if (previousState.sequenceId !== newMessageChunkSequenceId) {
      return {
        ...previousState,
        futureMessageChunks: [...previousState.futureMessageChunks, newMessageChunkPayload],
      }
    } else {
      let updatedState = processMessageChunk(previousState, newMessageChunkPayload)

      let continueProcessing = true

      // Try any other future messages that may now be in the correct sequence
      while (continueProcessing) {
        continueProcessing = false
        for (let i = 0; i < updatedState.futureMessageChunks.length; i++) {
          let futureMessageChunk = updatedState.futureMessageChunks[i]
          if (futureMessageChunk.sequence_id === updatedState.sequenceId) {
            updatedState = processMessageChunk(updatedState, futureMessageChunk)

            // we found a valid future message, keep trying to find more
            continueProcessing = true

            // go back and start from the first remaining message again
            // this should also help to avoid strange indexing issues where the
            // futureMessageChunks array is modified while we are iterating over it
            break
          }
        }
      }
      return updatedState
    }
  } else if (payload.type === ADA_STATUS_UPDATE) {
    const statusValue = payload.args[0]?.status
    if (typeof statusValue === 'string' && statusValue.trim().length > 0) {
      return {
        ...previousState,
        adaStatus: statusValue,
      }
    }
    return previousState
  } else if (payload.type === CLEAR_MESSAGES) {
    return {
      ...previousState,
      ...{
        messages: [],
        futureMessageChunks: [],
        sequenceId: 0,
      },
    }
  } else if (payload.type === REMOVE_LAST_MESSAGE) {
    const newFailedIndices = new Set<number>()
    previousState.failedMessageIndices.forEach((index) => {
      if (index < previousState.messages.length - 1) {
        newFailedIndices.add(index)
      }
    })
    return {
      ...previousState,
      messages: previousState.messages.slice(0, -1),
      failedMessageIndices: newFailedIndices,
    }
  } else if (payload.type === CLEAR_ACTIONS) {
    return {
      ...previousState,
      ...{
        actions: [],
      },
    }
  } else if (payload.type === SET_VOICE_SESSION_ACTIVE) {
    return {
      ...previousState,
      voiceSessionActive: payload.active,
    }
  } else if (payload.type === STOP_VOICE_SESSION) {
    return {
      ...previousState,
      voiceSessionActive: false,
      adaStatus: null,
    }
  } else if (payload.type === SET_APP_STATE) {
    return {
      ...previousState,
      appState: payload.appState,
    }
  } else if (payload.type === MARK_MESSAGES_AS_READ) {
    return {
      ...previousState,
      messages: previousState.messages.map((msg) => ({ ...msg, isRead: true })),
    }
  } else if (payload.type === IMPORT_VOICE_MESSAGES) {
    const importedMessages = payload.voiceMessages.map((msg) => ({
      ...msg,
      origin: 'voice' as const,
      sharedWithContexts: msg.sharedWithContexts || ['voice'],
    }))

    return {
      ...previousState,
      messages: [...previousState.messages, ...importedMessages],
    }
  } else if (payload.type === MARK_MESSAGES_SHARED_WITH_CONTEXT) {
    const { context, messageIndices } = payload

    return {
      ...previousState,
      messages: previousState.messages.map((msg, index) => {
        // If messageIndices is provided, only update those specific messages
        // Otherwise, update all messages that haven't been shared with this context
        const shouldUpdate = messageIndices ? messageIndices.includes(index) : !msg.sharedWithContexts.includes(context)

        if (shouldUpdate && !msg.sharedWithContexts.includes(context)) {
          return {
            ...msg,
            sharedWithContexts: [...msg.sharedWithContexts, context],
          }
        }
        return msg
      }),
    }
  } else if (payload.type === MARK_MESSAGE_FEEDBACK_SUBMITTED) {
    const newFeedbackSubmittedMessageIds = new Set(previousState.feedbackSubmittedMessageIds)
    newFeedbackSubmittedMessageIds.add(payload.messageId)
    return {
      ...previousState,
      feedbackSubmittedMessageIds: newFeedbackSubmittedMessageIds,
    }
  } else if (payload.type === MARK_MESSAGE_INDEX_FAILED) {
    const newFailedIndices = new Set(previousState.failedMessageIndices)
    newFailedIndices.add(payload.messageIndex)
    return {
      ...previousState,
      failedMessageIndices: newFailedIndices,
    }
  } else if (payload.type === CLEAR_MESSAGE_INDEX_FAILED) {
    const newFailedIndices = new Set(previousState.failedMessageIndices)
    newFailedIndices.delete(payload.messageIndex)
    return {
      ...previousState,
      failedMessageIndices: newFailedIndices,
    }
  } else if (payload.type === SET_SEND_FAILED) {
    return {
      ...previousState,
      sendFailed: true,
    }
  } else if (payload.type === CLEAR_SEND_FAILED) {
    return {
      ...previousState,
      sendFailed: false,
    }
  } else if (payload.type === UPDATE_TOOL_EXECUTION_STATE) {
    return {
      ...previousState,
      messages: previousState.messages.map((msg, index) => {
        if (index === payload.messageIndex) {
          return {
            ...msg,
            toolExecutionState: payload.toolExecutionState,
          }
        }
        return msg
      }),
    }
  } else if (payload.type === UPDATE_BUTTON_ACTION_STATE) {
    return {
      ...previousState,
      messages: previousState.messages.map((msg, index) => {
        if (index === payload.messageIndex) {
          return {
            ...msg,
            buttonActions: msg.buttonActions.map((buttonAction, buttonIndex) => {
              if (buttonIndex === payload.buttonActionIndex) {
                return {
                  ...buttonAction,
                  isCollapsed: payload.isCollapsed,
                  confirmedActionLabel: payload.confirmedActionLabel,
                }
              }
              return buttonAction
            }),
          }
        }
        return msg
      }),
    }
  } else if (payload.type === RESET_CHAT_STATE) {
    return {
      ...initialState,
    }
  } else if (payload.type === SET_CHAT_HEADER_MENU_OPEN) {
    return {
      ...previousState,
      chatHeaderMenuOpen: payload.open,
    }
  } else if (payload.type === SET_CHAT_SUGGESTIONS_PREFERENCES) {
    const updated = { ...previousState.chatSuggestionsPreferences, ...payload.preferences }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
    } catch (error) {
      console.error('Error updating chat suggestions preferences in localStorage:', error)
    }
    return {
      ...previousState,
      chatSuggestionsPreferences: updated,
    }
  }

  return previousState
}

export type ChatDrawerState = {
  chatDrawerOpen: boolean
  messages: ChatDrawerMessage[]
  futureMessageChunks: any[]
  sequenceId: number
  streaming: boolean
  actions: AdaAction[]
  voiceSessionActive: boolean
  appState: Record<string, any>
  adaStatus: string | null
  feedbackSubmittedMessageIds: Set<number>
  failedMessageIndices: Set<number>
  sendFailed: boolean
  chatHeaderMenuOpen: boolean
  chatSuggestionsPreferences: ChatSuggestionsPreferences
}

export const chatDrawerSelectors = {
  getChatDrawerOpen(state: RootState): boolean {
    return state.chatDrawer.chatDrawerOpen
  },
  getChatDrawerMessages(state: RootState): ChatDrawerMessage[] {
    return state.chatDrawer.messages
  },
  getAdaActions(state: RootState): AdaAction[] {
    return state.chatDrawer.actions
  },
  getVoiceSessionActive(state: RootState): boolean {
    return state.chatDrawer.voiceSessionActive
  },
  getAppState(state: RootState): Record<string, any> {
    return state.chatDrawer.appState
  },
  getUnreadCount(state: RootState): number {
    return state.chatDrawer.messages.filter((msg) => msg.direction === 'receive' && !msg.isRead).length
  },
  getHasUnreadMessages(state: RootState): boolean {
    return state.chatDrawer.messages.some((msg) => msg.direction === 'receive' && !msg.isRead)
  },
  getUnsharedContextQueue: createCachedReduxSelector((state: RootState): ChatDrawerMessage[] => {
    return state.chatDrawer.messages.filter((msg) => msg.sharedWithContexts.length === 0)
  }),
  getMessagesNotSharedWithContext: createCachedReduxSelector(
    (state: RootState, context: string): ChatDrawerMessage[] => {
      return state.chatDrawer.messages.filter((msg) => !msg.sharedWithContexts.includes(context))
    }
  ),
  getStreaming(state: RootState): boolean {
    return state.chatDrawer.streaming
  },
  getAdaStatus(state: RootState): string | null {
    return state.chatDrawer.adaStatus
  },
  getSendFailed(state: RootState): boolean {
    return state.chatDrawer.sendFailed
  },
  getFeedbackSubmittedMessageIds(state: RootState): Set<number> {
    return state.chatDrawer.feedbackSubmittedMessageIds
  },
  isMessageFeedbackSubmitted(state: RootState, messageId: number): boolean {
    return state.chatDrawer.feedbackSubmittedMessageIds.has(messageId)
  },
  getFailedMessageIndices(state: RootState): Set<number> {
    return state.chatDrawer.failedMessageIndices
  },
  getLatestReceivedMessageId(state: RootState): number | null {
    const receivedMessages = state.chatDrawer.messages.filter((msg) => msg.direction === 'receive')
    if (receivedMessages.length === 0) return null
    const latestMessage = receivedMessages[receivedMessages.length - 1]
    return latestMessage.messageId || null
  },
  getChatHeaderMenuOpen(state: RootState): boolean {
    return state.chatDrawer.chatHeaderMenuOpen
  },
  getChatSuggestionsPreferences(state: RootState): ChatSuggestionsPreferences {
    return state.chatDrawer.chatSuggestionsPreferences
  },
}
